import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { CLUB_TIERS } from "@/lib/constants";
import { toast } from "sonner";
import { Star, Sparkles, LogOut, Ticket as TicketIcon, Gift, Calendar, Settings as SettingsIcon, Trophy, Award, Users, Heart, Film, CreditCard, Baby } from "lucide-react";
import AboTab from "@/components/AboTab";
import FamilyTab from "@/components/FamilyTab";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); return d.toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function MemberDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState({ tickets: [], vouchers: [] });
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("card"); // card | tickets | vouchers | contests | profile
  const [contests, setContests] = useState([]);
  const reloadContests = () => api.get("/member/contests").then((r) => setContests(r.data)).catch(() => {});
  useEffect(() => { if (tab === "contests") reloadContests(); }, [tab]);
  const participate = async (c) => {
    try {
      const { data } = await api.post("/member/contests/participate", { contest_id: c.id });
      toast.success(`Teilnahme registriert · +${data.points_earned} Punkte${data.points_used ? ` (−${data.points_used})` : ""} · Neuer Stand: ${data.new_balance}`);
      // reload me + contests
      const [m, cs] = await Promise.all([api.get("/member/me"), api.get("/member/contests")]);
      setMe(m.data); setContests(cs.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  useEffect(() => {
    Promise.all([api.get("/member/me"), api.get("/member/history")])
      .then(([m, h]) => { setMe(m.data); setHistory(h.data); })
      .catch((e) => {
        if (e.response?.status === 401) nav("/mitglied/login", { replace: true });
        else setErr(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
      });
  }, [nav]);

  const logout = async () => {
    try { await api.post("/member/logout"); } catch { /* ignore */ }
    localStorage.removeItem("mh_member_token");
    nav("/mitglied/login", { replace: true });
  };

  if (err) return <div className="min-h-screen bg-black text-[#EE9CA1] flex items-center justify-center">{err}</div>;
  if (!me) return <div className="min-h-screen bg-black text-[#A8A398] flex items-center justify-center">Lade Club-Bereich …</div>;

  const tier = CLUB_TIERS.find((t) => t.v === me.tier) || CLUB_TIERS[0];
  const qrUrl = `${window.location.origin}/club/${me.member_qr || me.member_number}`;
  const guestName = `${me.first_name || ""} ${me.last_name || ""}`.trim();

  return (
    <div className="min-h-screen bg-black text-[#F5F0E6] relative overflow-hidden">
      {/* Backdrop */}
      {me.favorite_movie_backdrop_url && (
        <>
          <img src={me.favorite_movie_backdrop_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" style={{ filter: "blur(30px) saturate(1.1)" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/95" />
        </>
      )}

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-heading text-3xl text-[#D4AF37]" style={{ textShadow: "0 0 24px rgba(212,175,55,0.3)" }}>{me.cinema_name}</div>
            <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398] mt-1">{me.slogan}</div>
          </div>
          <button onClick={logout} data-testid="member-logout"
            className="flex items-center gap-2 py-2 px-4 rounded-full border border-[#2A2A2A] text-[10px] uppercase tracking-[0.28em] text-[#A8A398] hover:border-[#7A1921] hover:text-[#EE9CA1] transitions-safe">
            <LogOut size={13} /> Abmelden
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#1A1A1A] overflow-x-auto">
          {[
            { v: "card",      l: "Meine Karte",    icon: Star },
            { v: "abo",       l: "Mein Abo",       icon: CreditCard },
            { v: "family",    l: "Familie",        icon: Baby },
            { v: "tickets",   l: "Meine Tickets",  icon: TicketIcon },
            { v: "watchlist", l: "Merkliste",      icon: Heart },
            { v: "vouchers",  l: "Gutscheine",     icon: Gift },
            { v: "contests",  l: "Gewinnspiele",   icon: Trophy },
            { v: "points",    l: "Punkte-Verlauf", icon: Sparkles },
            { v: "profile",   l: "Profil",         icon: SettingsIcon },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`member-tab-${t.v}`}
              className={`flex items-center gap-2 py-3 px-4 text-[10px] uppercase tracking-[0.28em] border-b-2 transitions-safe whitespace-nowrap ${tab === t.v ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A8A398] hover:text-[#F5F0E6]"}`}>
              <t.icon size={14} /> {t.l}
            </button>
          ))}
        </div>

        {tab === "card" && (
          <div className="grid md:grid-cols-[380px_1fr] gap-6" data-testid="member-card-view">
            {/* Club-Karte */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0F0F0F] via-[#0A0A0A] to-black shadow-[0_18px_60px_rgba(0,0,0,0.75)]">
              <div className="absolute inset-0 rounded-2xl border border-[#8B7328]/50 pointer-events-none" />
              <div className="absolute inset-[5px] rounded-xl border pointer-events-none" style={{ borderColor: tier.color, opacity: 0.7 }} />
              <div className="h-2" style={{ background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)` }} />
              <div className="p-5 pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.42em] text-[#A8A398]">Movie House Club</div>
                    <div className="font-heading text-3xl mt-1 flex items-center gap-2" style={{ color: tier.color }}>
                      <Star size={18} fill={tier.color} strokeWidth={0} /> {tier.l}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-[0.35em] text-[#A8A398]">Besuche</div>
                    <div className="font-mono text-2xl" style={{ color: tier.color }}>{me.visits_count}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="font-heading text-xl leading-none truncate">{guestName}</div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">
                    <span>Nr.</span><span className="font-mono text-[#D4AF37]">{me.member_number}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <div className="bg-[#F5F0E6] p-2 rounded-lg">
                    <QRCodeCanvas value={qrUrl} size={130} bgColor="#F5F0E6" fgColor="#0A0A0A" />
                  </div>
                </div>
                <div className="mt-3 text-center text-[9px] uppercase tracking-[0.4em] text-[#575148]">
                  Beim Besuch vorzeigen
                </div>
              </div>
              <div className="h-1.5" style={{ background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)` }} />
            </div>

            {/* Vorteile & Aktionen */}
            <div className="space-y-4">
              <div className="card-lux p-5">
                <div className="text-[10px] uppercase tracking-[0.35em] flex items-center gap-2" style={{ color: tier.color }}>
                  <Sparkles size={14} /> Deine Vorteile
                </div>
                <ul className="mt-3 space-y-2">
                  {(me.benefits || []).map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tier.color }} />
                      <span>{b}</span>
                    </li>
                  ))}
                  {!(me.benefits || []).length && <li className="text-sm text-[#A8A398]">Willkommen im Club!</li>}
                </ul>
              </div>
              {me.favorite_movie_title && (
                <div className="card-lux p-5 flex items-center gap-4">
                  {me.favorite_movie_poster_url && <img src={me.favorite_movie_poster_url} alt="" className="w-16 h-24 object-cover rounded" />}
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.4em] text-[#A8A398]">Dein Lieblingsfilm</div>
                    <div className="font-heading text-2xl text-[#D4AF37] mt-1">{me.favorite_movie_title}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "tickets" && <TicketsList tickets={history.tickets} />}
        {tab === "abo" && <AboTab />}
        {tab === "family" && <FamilyTab />}
        {tab === "vouchers" && <VouchersList vouchers={history.vouchers} />}
        {tab === "watchlist" && <WatchlistTab />}
        {tab === "contests" && <ContestsList contests={contests} points={me.points_balance || 0} onParticipate={participate} />}
        {tab === "points" && <PointsHistoryTab />}
        {tab === "profile" && <ProfileTab me={me} onUpdated={(patch) => setMe({ ...me, ...patch })} />}
      </div>
    </div>
  );
}

function WatchlistTab() {
  const [list, setList] = useState([]);
  const [reco, setReco] = useState({ recommendations: [], top_genres: [] });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: w }, { data: r }] = await Promise.all([api.get("/me/watchlist"), api.get("/me/recommendations")]);
      setList(w || []); setReco(r || { recommendations: [], top_genres: [] });
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.delete(`/me/watchlist/${id}`); load(); } catch { /* noop */ } };
  const add = async (id) => { try { await api.post(`/me/watchlist/${id}`); load(); toast.success("Auf Merkliste"); } catch { /* noop */ } };
  if (loading) return <div className="text-center py-10 text-[#A8A398]">Lade Merkliste …</div>;
  return (
    <div className="space-y-8" data-testid="member-watchlist-tab">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] flex items-center gap-2"><Heart size={14} /> Meine Merkliste · {list.length}</div>
        </div>
        {list.length === 0 ? (
          <div className="card-lux p-8 text-center text-[#A8A398] text-sm">Noch keine Filme gemerkt. Öffne einen Filmdetail auf der Startseite und klicke „Auf Merkliste".</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {list.map((m) => (
              <div key={m.id} className="card-lux p-3 relative group" data-testid={`watchlist-item-${m.id}`}>
                {m.poster_url ? <img src={m.poster_url} alt="" className="w-full aspect-[2/3] object-cover rounded" /> : <div className="w-full aspect-[2/3] rounded bg-[#141414] flex items-center justify-center text-[#575148]"><Film size={30} /></div>}
                <div className="mt-2 text-sm truncate">{m.title_de || m.title_original}</div>
                <div className="text-[10px] text-[#575148]">{m.duration_min ? `${m.duration_min} min` : ""}{m.year ? ` · ${m.year}` : ""}</div>
                <button onClick={() => remove(m.id)} title="Entfernen" className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/70 text-[#EE9CA1] rounded-full w-6 h-6 flex items-center justify-center transitions-safe">×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {(reco.recommendations || []).length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#EEDC82] flex items-center gap-2"><Sparkles size={14} /> Für dich empfohlen</div>
            {(reco.top_genres || []).length > 0 && (
              <div className="text-[10px] text-[#A8A398]">Basierend auf: {reco.top_genres.map((g) => g.genre).join(" · ")}</div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {reco.recommendations.slice(0, 8).map((m) => (
              <div key={m.id} className="card-lux p-3 relative">
                {m.poster_url ? <img src={m.poster_url} alt="" className="w-full aspect-[2/3] object-cover rounded" /> : <div className="w-full aspect-[2/3] rounded bg-[#141414] flex items-center justify-center text-[#575148]"><Film size={30} /></div>}
                <div className="mt-2 text-sm truncate">{m.title_de || m.title_original}</div>
                <div className="text-[10px] text-[#575148]">{m.genre || (m.genres || []).slice(0,2).join(" · ")}</div>
                <button onClick={() => add(m.id)} data-testid={`reco-add-${m.id}`} className="mt-2 w-full text-[10px] uppercase tracking-widest py-1.5 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20"><Heart size={11} className="inline mr-1" /> Merken</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TicketsList({ tickets }) {
  if (!tickets.length) return (
    <div className="card-lux p-10 text-center text-[#A8A398]" data-testid="member-tickets-empty">
      <TicketIcon size={40} className="mx-auto text-[#575148] mb-3" />
      Noch keine Tickets. Erste Vorstellung <Link to="/program" className="text-[#D4AF37] underline">hier buchen</Link>.
    </div>
  );
  return (
    <div className="space-y-3" data-testid="member-tickets-list">
      {tickets.map((t) => (
        <div key={t.id} className="card-lux p-4 flex items-center gap-4">
          {t.movie_poster_url ? (
            <img src={t.movie_poster_url} alt="" className="w-14 h-20 object-cover rounded" />
          ) : (
            <div className="w-14 h-20 bg-[#0F0F0F] rounded flex items-center justify-center"><TicketIcon size={22} className="text-[#575148]" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg text-[#F5F0E6] truncate">{t.movie_title}</div>
            <div className="text-xs text-[#A8A398] mt-0.5 flex items-center gap-2 flex-wrap">
              <span><Calendar size={11} className="inline mr-1" />{fmtDate(t.date)} · {t.start_time}</span>
              <span>· {t.hall}</span>
              <span>· Sitz {t.seat}</span>
              <span>· {t.status || "aktiv"}</span>
            </div>
          </div>
          <a href={`/ticket/${t.token}`} target="_blank" rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.28em] px-3 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
            Anzeigen
          </a>
        </div>
      ))}
    </div>
  );
}

function VouchersList({ vouchers }) {
  if (!vouchers.length) return (
    <div className="card-lux p-10 text-center text-[#A8A398]" data-testid="member-vouchers-empty">
      <Gift size={40} className="mx-auto text-[#575148] mb-3" />
      Keine Gutscheine hinterlegt.
    </div>
  );
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="member-vouchers-list">
      {vouchers.map((v) => (
        <div key={v.id} className="card-lux p-4">
          <div className="flex items-center justify-between">
            <div className="text-[9px] uppercase tracking-[0.35em] text-[#D4AF37]">{v.kind === "ticket_pack" ? "10er-Karte" : "Geldgutschein"}</div>
            <div className="text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded-full px-2 py-0.5">{v.status || "aktiv"}</div>
          </div>
          <div className="mt-2 font-heading text-2xl text-[#D4AF37]">
            {v.kind === "ticket_pack" ? `${v.ticket_uses_remaining ?? 0}/${v.ticket_count ?? 10} Eintritte` : `${(v.remaining_value ?? v.value ?? 0).toFixed(2)} €`}
          </div>
          <div className="mt-3 text-xs text-[#A8A398] font-mono">{v.code}</div>
          {v.valid_until && <div className="text-[10px] text-[#575148] mt-1">Gültig bis {fmtDate(v.valid_until)}</div>}
        </div>
      ))}
    </div>
  );
}

function ContestsList({ contests, points, onParticipate }) {
  return (
    <div className="space-y-4" data-testid="member-contests-tab">
      <div className="card-lux p-5 flex items-center gap-4 bg-gradient-to-r from-[#1A1204] to-transparent">
        <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
          <Sparkles size={26} className="text-[#D4AF37]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Dein Punktestand</div>
          <div className="font-heading text-3xl text-[#D4AF37]" data-testid="member-points">{points} Punkte</div>
          <div className="text-[10px] text-[#575148] mt-1 italic">+10 Punkte für jeden Kinobesuch, extra Punkte durch Teilnahme an Gewinnspielen.</div>
        </div>
      </div>
      {!contests.length ? (
        <div className="card-lux p-10 text-center text-[#A8A398]">
          <Trophy size={40} className="mx-auto text-[#575148] mb-3" />
          Aktuell laufen keine Gewinnspiele — schau bald wieder rein!
        </div>
      ) : contests.map((c) => (
        <div key={c.id} className="card-lux overflow-hidden" data-testid={`contest-item-${c.id}`}>
          {c.prize_image_url && <img src={c.prize_image_url} alt="" className="w-full h-40 object-cover" />}
          <div className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-heading text-2xl">{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                +{c.points_reward} Punkte
                {c.points_cost ? ` · Kosten ${c.points_cost}` : ""}
              </div>
            </div>
            {c.description && <p className="text-sm text-[#A8A398] mt-2">{c.description}</p>}
            <div className="mt-3 flex items-center gap-2 text-sm">
              <Award size={14} className="text-[#EEDC82]" /> <span className="text-[#EEDC82]">{c.prize_description}</span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-[10px] text-[#575148] uppercase tracking-widest">
              {c.end_at && <span>Endet: {new Date(c.end_at).toLocaleDateString("de-DE")}</span>}
              <span><Users size={10} className="inline mr-1" />{c.total_entries} Teilnahmen</span>
              <span>Deine Teilnahmen: {c.my_entries}/{c.max_entries_per_member}</span>
            </div>
            <button onClick={() => onParticipate(c)} disabled={!c.can_participate} data-testid={`contest-participate-${c.id}`}
              className={`mt-4 px-5 py-2 rounded text-[11px] uppercase tracking-widest transitions-safe ${c.can_participate ? "bg-[#D4AF37] hover:bg-[#EEDC82] text-black" : "bg-[#1A1A1A] text-[#575148] cursor-not-allowed"}`}>
              {c.can_participate ? (c.points_cost ? `Für ${c.points_cost} Punkte teilnehmen` : "Jetzt teilnehmen") : "Bereits teilgenommen"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PointsHistoryTab() {
  const [month, setMonth] = React.useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get(`/member/points/history?month=${month}`).then((r) => setData(r.data)).catch(() => setData({ transactions: [], balance: 0 }));
  }, [month]);
  if (!data) return <div className="text-[#A8A398] text-center py-10">Lade Punkte …</div>;
  const SRC_LABEL = { visit: "Kinobesuch", purchase: "Kauf", contest_reward: "Gewinnspiel", contest_cost: "Teilnahme", adjustment: "Anpassung", winner: "Gewinn" };
  const SRC_COLOR = { visit: "#D4AF37", purchase: "#EEDC82", contest_reward: "#D4AF37", contest_cost: "#EE9CA1", winner: "#D4AF37" };
  return (
    <div className="space-y-4" data-testid="member-points-tab">
      <div className="card-lux p-5 grid md:grid-cols-3 gap-4 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Punktestand</div>
          <div className="font-heading text-4xl text-[#D4AF37]">{data.balance}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Gesammelt (Monat)</div>
          <div className="font-heading text-2xl text-[#EEDC82]">+ {data.period_earned}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Verwendet (Monat)</div>
          <div className="font-heading text-2xl text-[#EE9CA1]">− {data.period_spent}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Monat</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="points-month-picker"
          className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm text-[#F5F0E6] focus:border-[#D4AF37] outline-none" />
      </div>
      {!data.transactions.length ? (
        <div className="card-lux p-10 text-center text-[#A8A398]">
          Für {month} sind keine Punkte-Bewegungen erfasst.
        </div>
      ) : (
        <div className="card-lux divide-y divide-[#151515]" data-testid="points-transactions">
          {data.transactions.map((tx) => (
            <div key={tx.id} className="flex items-center gap-4 py-3 px-4">
              <div className="text-[10px] uppercase tracking-widest text-[#575148] w-24 shrink-0">
                {new Date(tx.at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                <br />{new Date(tx.at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{tx.description}</div>
                <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: SRC_COLOR[tx.source] || "#A8A398" }}>
                  {SRC_LABEL[tx.source] || tx.source}
                </div>
              </div>
              <div className={`font-mono text-lg ${tx.points >= 0 ? "text-[#D4AF37]" : "text-[#EE9CA1]"}`}>
                {tx.points >= 0 ? "+" : ""}{tx.points}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileTab({ me, onUpdated }) {
  const [phone, setPhone] = useState(me.phone || "");
  const [birthday, setBirthday] = useState(me.birthday || "");
  const [newsletter, setNewsletter] = useState(!!me.newsletter_subscribed);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/member/me", { phone, birthday, newsletter_subscribed: newsletter });
      onUpdated({ phone, birthday, newsletter_subscribed: newsletter });
      toast.success("Gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-lux p-6 max-w-xl" data-testid="member-profile-tab">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorname</label>
          <input value={me.first_name} disabled className="w-full mt-1 bg-[#050505] border border-[#1A1A1A] rounded px-3 py-2 opacity-70" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Nachname</label>
          <input value={me.last_name} disabled className="w-full mt-1 bg-[#050505] border border-[#1A1A1A] rounded px-3 py-2 opacity-70" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">E-Mail</label>
          <input value={me.email} disabled className="w-full mt-1 bg-[#050505] border border-[#1A1A1A] rounded px-3 py-2 opacity-70" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Telefon</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="member-profile-phone"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Geburtstag</label>
          <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} data-testid="member-profile-birthday"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Newsletter</label>
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} data-testid="member-profile-newsletter" className="accent-[#D4AF37]" />
            <span className="text-sm">Filmnews & Events per E-Mail</span>
          </label>
        </div>
      </div>
      <button onClick={save} disabled={busy} data-testid="member-profile-save"
        className="mt-6 bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-2 px-6 rounded text-[11px] uppercase tracking-widest disabled:opacity-50">
        {busy ? "…" : "Speichern"}
      </button>
    </div>
  );
}
